<div class="email-detail-container">
  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-container" role="status">
    <div class="loading-spinner" aria-hidden="true"></div>
    <p>{{ 'ED_LOADING' | translate }}</p>
  </div>

  <!-- Error State -->
  <div *ngIf="!isLoading && errorMessage" class="not-found-container">
    <h2>{{ errorMessage | translate }}</h2>
    <button class="back-btn" (click)="retry()">{{ 'EC_RETRY' | translate }}</button>
    <button class="back-btn" (click)="goBack()">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <polyline points="15,18 9,12 15,6"></polyline>
      </svg>
      {{ 'ED_BACK_TO_INBOX' | translate }}
    </button>
  </div>

  <!-- ════════════════════════════════════════════════════════════════════ -->
  <!-- BLACKLISTED EMAIL VIEW (simple — router state or intake-thread API) -->
  <!-- ════════════════════════════════════════════════════════════════════ -->
  <div *ngIf="!isLoading && !errorMessage && isBlacklisted && blacklistedEmail" class="email-thread-viewer">
    <div class="detail-header">
      <button class="btn-back" (click)="goBack()">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="15,18 9,12 15,6"></polyline></svg>
        {{ 'ED_BACK' | translate }}
      </button>
      <h1 class="detail-title">{{ blacklistedEmail.subject }}</h1>
    </div>

    <!-- Status Banner -->
    <div class="blacklisted-banner">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
      </svg>
      <span *ngIf="blacklistStatusKey">{{ 'ED_BLACKLISTED_BANNER' | translate: { status: (blacklistStatusKey | translate) } }}</span>
      <span *ngIf="!blacklistStatusKey">{{ 'ED_BLACKLISTED_BANNER_GENERIC' | translate }}</span>
    </div>

    <!-- Simple Email Card -->
    <div class="email-card original-email">
      <div class="email-header">
        <h2 class="email-subject">{{ blacklistedEmail.subject }}</h2>
      </div>

      <div class="sender-section">
        <div class="sender-avatar citizen-avatar" aria-hidden="true">
          <span>{{ getSenderInitials(blacklistedEmail.senderDisplayName, blacklistedEmail.senderEmail) }}</span>
        </div>
        <div class="sender-info">
          <div class="sender-main">
            <span class="sender-name">{{ blacklistedEmail.senderDisplayName }}</span>
            <span class="sender-email">&lt;{{ blacklistedEmail.senderEmail }}&gt;</span>
          </div>
        </div>
        <div class="email-date">
          {{ blacklistedEmail.receivedAt | date:'MMM d, y, h:mm a' }}
        </div>
      </div>

      <div class="email-body" *ngIf="blacklistedEmail.bodySnippet">
        <div class="email-content" dir="auto">{{ blacklistedEmail.bodySnippet }}</div>
      </div>

      <div class="email-body" *ngIf="!blacklistedEmail.bodySnippet">
        <div class="email-content no-content">{{ 'ED_NO_BODY' | translate }}</div>
      </div>
    </div>
  </div>

  <!-- ════════════════════════════════════════════════════════════════════ -->
  <!-- THREAD VIEW (whitelisted / greylisted — data from Thread API)      -->
  <!-- ════════════════════════════════════════════════════════════════════ -->
  <div *ngIf="!isLoading && !errorMessage && !isBlacklisted && thread" class="email-thread-viewer">
    <div class="detail-header">
      <button class="btn-back" (click)="goBack()">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="15,18 9,12 15,6"></polyline></svg>
        {{ 'ED_BACK' | translate }}
      </button>
      <h1 class="detail-title">{{ originalMessage?.subject || ('ED_EMAIL_THREAD' | translate) }}</h1>
    </div>

    <!-- Draft Metadata Panel -->
    <div class="metadata-panel">
      <div class="metadata-row">
        <div class="metadata-item" *ngIf="thread.intentGroup">
          <span class="metadata-label">{{ 'EC_INTENT' | translate }}</span>
          <span class="metadata-value">{{ 'EC_INTENT_' + (thread.intentGroup | uppercase) | translate }}</span>
        </div>
        <!-- <div class="metadata-item">
          <span class="metadata-label">AI Confidence</span>
          <span class="metadata-value confidence-badge" [ngClass]="getConfidenceClass()">
            {{ (thread.aiConfidence * 100) | number:'1.0-0' }}% ({{ getConfidenceLabel() }})
          </span>
        </div> -->
        <div class="metadata-item" *ngIf="statusKey">
          <span class="metadata-label">{{ 'ED_STATUS' | translate }}</span>
          <span class="metadata-value status-badge" [ngClass]="{
            'status-complete': thread.parsingStatus === 'COMPLETE',
            'status-pending': thread.parsingStatus !== 'COMPLETE'
          }">
            {{ statusKey | translate }}
          </span>
        </div>
        <div class="metadata-item" *ngIf="thread.needsReview">
          <span class="metadata-label">{{ 'EC_REVIEW' | translate }}</span>
          <span class="metadata-value review-badge">⚠ {{ 'NEEDS_REVIEW' | translate }}</span>
        </div>
      </div>

      <!-- Missing Fields Alert -->
      <div class="missing-fields-alert" *ngIf="missingFieldsList.length > 0">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>{{ 'ED_MISSING_FIELDS_LABEL' | translate }} <strong>{{ missingFieldsList.join(', ') }}</strong></span>
      </div>

      <!-- Extracted Data Summary -->
      <div class="extracted-summary" *ngIf="thread.name || thread.cnic || thread.mobile">
        <span class="summary-item" *ngIf="thread.name">
          <strong>{{ 'ED_NAME' | translate }}:</strong> {{ thread.name }}
        </span>
        <span class="summary-item" *ngIf="thread.cnic">
          <strong>{{ 'ED_CNIC' | translate }}:</strong> {{ thread.cnic }}
        </span>
        <span class="summary-item" *ngIf="thread.mobile">
          <strong>{{ 'ED_MOBILE' | translate }}:</strong> {{ thread.mobile }}
        </span>
        <span class="summary-item" *ngIf="thread.districtName">
          <strong>{{ 'ED_DISTRICT' | translate }}:</strong> {{ thread.districtName }}
        </span>
      </div>
    </div>

    <!-- Original Email Card -->
    <div class="email-card original-email" *ngIf="originalMessage as original">
      <div class="email-header">
        <h2 class="email-subject">{{ original.subject }}</h2>
      </div>

      <div class="sender-section">
        <div class="sender-avatar citizen-avatar" aria-hidden="true">
          <span>{{ getSenderInitials(original.senderDisplayName, original.senderEmail) }}</span>
        </div>
        <div class="sender-info">
          <div class="sender-main">
            <span class="sender-name">{{ original.senderDisplayName }}</span>
            <span class="sender-email">&lt;{{ original.senderEmail }}&gt;</span>
          </div>
          <div class="message-type-label">{{ 'ED_ORIGINAL_EMAIL' | translate }}</div>
        </div>
        <div class="email-date">
          {{ original.receivedAt | date:'MMM d, y, h:mm a' }}
        </div>
      </div>

      <div class="email-body">
        <div class="email-content" dir="auto">{{ original.bodyText }}</div>
      </div>
    </div>

    <!-- Reply Messages (threaded conversation) -->
    <div *ngFor="let message of replyMessages; trackBy: trackByMessage" class="email-card reply-card"
      [ngClass]="{ 'system-reply': isSystemMessage(message), 'citizen-reply': !isSystemMessage(message) }">
      <div class="sender-section">
        <div class="sender-avatar" aria-hidden="true"
          [ngClass]="{ 'system-avatar': isSystemMessage(message), 'citizen-avatar': !isSystemMessage(message) }">
          <span>{{ isSystemMessage(message) ? 'AI' : getSenderInitials(message.senderDisplayName, message.senderEmail) }}</span>
        </div>
        <div class="sender-info">
          <div class="sender-main">
            <span class="sender-name">{{ message.senderDisplayName }}</span>
            <span class="sender-email">&lt;{{ message.senderEmail }}&gt;</span>
          </div>
          <div class="message-type-label">
            {{ (isSystemMessage(message) ? 'ED_SYSTEM_REPLY' : 'ED_CITIZEN_REPLY') | translate }}
          </div>
        </div>
        <div class="email-date">
          {{ message.receivedAt | date:'MMM d, y, h:mm a' }}
        </div>
      </div>

      <div class="email-body">
        <div class="email-content" dir="auto">{{ message.bodyText }}</div>
      </div>
    </div>

    <!-- Reply Section -->
    <div class="reply-section" *ngIf="originalMessage">
      <div class="reply-header">
        <div class="reply-avatar">
          <span>U</span>
        </div>
        <span class="reply-label">{{ 'ED_REPLY_TO' | translate }} {{ originalMessage.senderDisplayName || originalMessage.senderEmail }}</span>
      </div>

      <div class="reply-compose">
        <textarea class="reply-input" [placeholder]="'ED_REPLY_PLACEHOLDER' | translate" rows="4"
          [(ngModel)]="replyText" [disabled]="isSendingReply"></textarea>

        <!-- Selected attachments -->
        <div class="reply-attachments" *ngIf="replyFiles.length > 0">
          <span class="reply-file-chip" *ngFor="let file of replyFiles; let i = index">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
            <span class="reply-file-name">{{ file.name }}</span>
            <button type="button" class="reply-file-remove" (click)="removeReplyFile(i)" [disabled]="isSendingReply" title="Remove">&#x2715;</button>
          </span>
        </div>

        <div class="reply-toolbar">
          <button type="button" class="attach-button" (click)="triggerReplyFileInput()" [disabled]="isSendingReply"
            [title]="'ED_REPLY_ATTACH' | translate">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>
          <span class="reply-feedback reply-feedback-success" *ngIf="replySuccess">{{ replySuccess | translate }}</span>
          <span class="reply-feedback reply-feedback-error" *ngIf="replyError">{{ replyError | translate }}</span>
          <button class="send-button" (click)="sendReply()" [disabled]="!replyText.trim() || isSendingReply">
            {{ (isSendingReply ? 'ED_REPLY_SENDING' : 'ED_REPLY_SEND') | translate }}
          </button>
        </div>

        <input #replyFileInput type="file" multiple (change)="onReplyFilesSelected($event)" style="display: none" />
      </div>
    </div>
  </div>
</div>
